import React, { Fragment, useCallback } from 'react';
import { TelemetryPanel } from '../lib/dashboard-types';

import {
  TelemetryLayout,
  TelemetryPanelWrapper,
  TelemetryCard,
  TelemetryCardHeader,
  TelemetryCardTitle,
  TelemetryCardAlertBadge,
  TelemetryCardVizSelect,
  TelemetryCardSearchInput,
  TelemetryCardActions,
  TelemetryCardExpandButton,
  TelemetryCardMenuButton,
  TelemetryCardBody,
  TelemetryCardChart,
  getTelemetrySubColor,
  getPanelPrimaryParam,
  resolveTableColumns,
  tableColDef,
  unitOf,
  GapZone,
  TelemetryLayoutMode,
} from '../../../../../CommonComponents/telemetry';
import useTelemetry from '../hooks/useTelemetry';
import { useDashboardSettings } from '../hooks/useDashboardSettings';
import { useViewport } from '../hooks/useViewport';
import { useMenu } from '../hooks/useMenu';
import useWidgetMenu from '../hooks/useWidgetMenu';
import './telemetry-components.css';
import MaximizeModal from './maximize-modal';

export interface GridDashboardProps {
  layout?: TelemetryPanel[];
  setLayout?: (layout: TelemetryPanel[]) => void;
  layoutMode?: TelemetryLayoutMode;
  onOpenAdd?: () => void;
}

export const GridDashboard: React.FC<GridDashboardProps> = (props) => {
  const {
    activeLayout,
    setLayout: setTelemetryLayout,
    patchWidget,
    telemetrySeries,
    isTelemetryDataLoading,
    setExpandedTelemetryPanel,
    setInsertGap,
    setAddModalOpen,
  } = useTelemetry();

  const {
    layoutMode: defaultLayoutMode,
    timeframe,
    customStartTimeSec,
    precision,
    units,
  } = useDashboardSettings();

  const {
    viewport,
    setViewport,
    viewportLabel,
    isPaused,
    isAtStartBoundary,
    isLoadingHistory,
    jumpToLive,
    getChartWrapRef,
  } = useViewport(timeframe);

  const { menu } = useMenu();
  const { openWidgetMenu, handleVizClickMenu } = useWidgetMenu();

  const layout = props.layout ?? activeLayout;
  const setLayout = props.setLayout ?? setTelemetryLayout;
  const layoutMode = props.layoutMode ?? defaultLayoutMode;
  const onOpenAdd = props.onOpenAdd ?? (() => setAddModalOpen(true));

  const handleGapInsert = useCallback(
    (gap: GapZone) => {
      setInsertGap(gap);
      setAddModalOpen(true);
    },
    [setInsertGap, setAddModalOpen]
  );

  return (
    <TelemetryLayout
      layout={layout}
      onLayoutChange={setLayout}
      telemetrySeries={telemetrySeries}
      layoutMode={layoutMode}
      timeframe={timeframe}
      customStartTimeSec={customStartTimeSec}
      precision={precision}
      units={units}
      viewport={viewport}
      onViewportChange={setViewport}
      viewportLabel={viewportLabel}
      isPaused={isPaused}
      isAtStartBoundary={isAtStartBoundary}
      isLoadingHistory={isLoadingHistory}
      isTelemetryDataLoading={isTelemetryDataLoading}
      onJumpLive={jumpToLive}
      getChartWrapRef={getChartWrapRef}
      patchWidget={patchWidget}
      activeMenuTriggerId={menu?.triggerId}
      onGapInsert={handleGapInsert}
      onOpenAdd={onOpenAdd}
    >
      {(layoutCtx) => (
        <Fragment>
          {layout.map((panel, panelIdx) => {
            const primaryParam = getPanelPrimaryParam(panel);
            const subColor = getTelemetrySubColor(primaryParam.subsystem);
            const cols = resolveTableColumns(panel);
            const uniqueUnits = new Set<string>();
            cols.forEach((c) => {
              const def = tableColDef(c);
              const u = unitOf(def, units) || (def?.unit ? (def.unit.trim().toLowerCase() === 'none' ? '' : def.unit) : '');
              if (u) uniqueUnits.add(u);
            });
            const numUnits = uniqueUnits.size;
            const totalSeries = cols.length;
            const totalParams = panel.parameters?.length || 1;
            const seriesLabel = totalParams > 1 && totalParams !== totalSeries
              ? `${totalSeries} series (${totalParams} params)`
              : `${totalSeries} series`;
            const isMultiStream = panel.graphMode === 'multi' || panel.graphMode === 'cchart' || ((panel.graphMode === 'line' || panel.graphMode === 'area') && totalSeries > 1);
            const subtitleMeta = isMultiStream && totalSeries > 1
              ? (numUnits > 0 ? `${seriesLabel} · ${numUnits} unit${numUnits > 1 ? 's' : ''}` : seriesLabel)
              : undefined;

            return (
              <TelemetryPanelWrapper
                key={panel.id}
                panel={panel}
                index={panelIdx}
                onGapInsert={handleGapInsert}
              >
                <TelemetryCard
                  panel={panel}
                  isDrag={layoutCtx.dragId === panel.id}
                  isResize={layoutCtx.resizeId === panel.id}
                  onResizeDown={(dir, e) => layoutCtx.onResizeDown(panel.id, dir, e)}
                  onContextMenu={(e) => { e.preventDefault(); openWidgetMenu(panel, e, true); }}
                >
                  <TelemetryCardHeader
                    panel={panel}
                    onDragDown={(e) => layoutCtx.onDragDown(panel.id, e)}
                  >
                    <TelemetryCardTitle subColor={subColor} subtitle={subtitleMeta}>
                      {panel.name || primaryParam.label}
                    </TelemetryCardTitle>
                    <TelemetryCardAlertBadge panel={panel} />

                    <TelemetryCardActions>
                      <TelemetryCardVizSelect
                        panel={panel}
                        activeMenuTriggerId={menu?.triggerId}
                        onVizClick={(e) => handleVizClickMenu(panel, e, primaryParam.subsystem, primaryParam.paramName)}
                      />
                      <TelemetryCardSearchInput panel={panel} />
                      <TelemetryCardExpandButton panel={panel} onExpand={() => setExpandedTelemetryPanel(panel)} />
                      <TelemetryCardMenuButton panel={panel} onClick={(e) => openWidgetMenu(panel, e, false)} />
                    </TelemetryCardActions>
                  </TelemetryCardHeader>

                  <TelemetryCardBody>
                    <TelemetryCardChart panel={panel} />
                  </TelemetryCardBody>
                </TelemetryCard>
              </TelemetryPanelWrapper>
            );
          })}
          <MaximizeModal />
        </Fragment>
      )}
    </TelemetryLayout>
  );
};

export default GridDashboard;
